import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { EquipmentTypeField } from '@/components/EquipmentTypeSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { appTaskScreenConfigState, equipmentTypesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { zodResolver } from '@hookform/resolvers/zod';
import { TaskTypeDao } from '@rock/db-admin';
import type {
    EquipmentType,
    EquipmentTypeId,
    SpaceId,
    TaskScreenSection,
    TaskType,
    TaskTypeScreenConfig,
    TaskTypeScreenSection,
} from '@rock/types';
import { INITIAL_TASK_SCREEN_CONFIG, TASK_SECTION_LABELS } from '@rock/tasks';
import { ArrowDown, ArrowUp, CircleX, Info, PlusCircle, RotateCcw } from 'lucide-react';
import { type FC, useCallback } from 'react';
import { type UseFormReturn, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

const log = logger('TaskTypeScreenConfigTab');
const taskTypeDao = new TaskTypeDao();

// Flat form schema — all fields present, form logic handles visibility
const FormSectionSchema = z.object({
    type: z.enum(['instructions', 'history', 'pictures', 'notes', 'supplies', 'contact']),
    enabled: z.boolean(),
    useDefault: z.boolean(),
    title: z.string().optional(),
    description: z.string().optional(),
    slots: z
        .array(
            z.object({
                category: z.enum(['before', 'after', 'other']),
                title: z.string().min(1, 'Label is required'),
            }),
        )
        .optional(),
    suppliesConfig: z
        .array(
            z.object({
                equipmentTypeId: z.string(),
                quantityPerTask: z.number(),
            }),
        )
        .optional(),
});

const FormSchema = z.object({
    sections: z.array(FormSectionSchema),
});

type FormValues = z.infer<typeof FormSchema>;

/**
 * Convert a default section into a form section with useDefault=true.
 * Uses `defaultEnabled` (if present on app-level sections) for the initial enabled state,
 * matching what task generation would produce.
 */
function defaultToFormSection(s: TaskScreenSection & { defaultEnabled?: boolean }): FormValues['sections'][number] {
    return {
        type: s.type,
        enabled: s.defaultEnabled ?? s.enabled,
        useDefault: true,
        title: s.title,
        description: s.description,
        slots: s.type === 'pictures' ? s.slots : undefined,
        suppliesConfig: s.type === 'supplies' ? s.suppliesConfig : undefined,
    };
}

/**
 * Convert TaskTypeScreenConfig (or default) into flat form values.
 * When a config exists, any sections from `defaultSections` that are missing
 * are appended with useDefault=true so the admin can see and configure them.
 */
function toFormValues(config: TaskTypeScreenConfig | undefined, defaultSections: TaskScreenSection[]): FormValues {
    if (config) {
        const coveredTypes = new Set(config.sections.map((s) => s.type));
        const mapped: FormValues['sections'] = config.sections.map((s) => ({
            type: s.type,
            enabled: s.enabled,
            useDefault: s.useDefault,
            title: s.title,
            description: s.description,
            slots: s.type === 'pictures' ? s.slots : undefined,
            suppliesConfig: s.type === 'supplies' ? s.suppliesConfig : undefined,
        }));

        // Append new default sections that this TaskType doesn't have yet
        for (const ds of defaultSections) {
            if (!coveredTypes.has(ds.type)) {
                mapped.push(defaultToFormSection(ds));
            }
        }

        return { sections: mapped };
    }

    // No config: derive from defaults with useDefault=true
    return {
        sections: defaultSections.map(defaultToFormSection),
    };
}

/**
 * Convert form values back to the proper TaskTypeScreenConfig discriminated union.
 */
function toTaskTypeScreenConfig(values: FormValues): TaskTypeScreenConfig {
    const sections: TaskTypeScreenSection[] = values.sections.map((s) => {
        const base = {
            enabled: s.enabled,
            useDefault: s.useDefault,
            title: s.useDefault ? undefined : s.title || undefined,
            description: s.useDefault ? undefined : s.description || undefined,
        };
        switch (s.type) {
            case 'pictures':
                return {
                    ...base,
                    type: 'pictures' as const,
                    slots: s.useDefault ? undefined : s.slots,
                };
            case 'supplies':
                return {
                    ...base,
                    type: 'supplies' as const,
                    suppliesConfig: s.useDefault
                        ? undefined
                        : s.suppliesConfig?.map((sc) => ({
                              ...sc,
                              equipmentTypeId: sc.equipmentTypeId as EquipmentTypeId,
                          })),
                };
            default:
                return { ...base, type: s.type };
        }
    });
    return { sections };
}

export const TaskTypeScreenConfigTab: FC<{ taskType: TaskType; spaceId?: SpaceId }> = ({ taskType, spaceId }) => {
    const appConfig = useAsyncAtomValue(appTaskScreenConfigState, undefined);
    const defaultSections = appConfig?.sections ?? INITIAL_TASK_SCREEN_CONFIG.sections;
    const allEquipmentTypes = useAsyncAtomValue(equipmentTypesState(spaceId ?? ('' as SpaceId)), []);
    const suppliesTypes = allEquipmentTypes.filter((t) => t.scope === 'supplies');

    const form = useForm<FormValues, unknown, FormValues>({
        resolver: zodResolver(FormSchema),
        defaultValues: toFormValues(taskType.taskScreenConfig, defaultSections),
    });

    const sections = useFieldArray({
        control: form.control,
        name: 'sections',
    });

    const onSubmit = useCallback(
        async (values: FormValues) => {
            try {
                const taskScreenConfig = toTaskTypeScreenConfig(values);
                const updated: TaskType = {
                    ...taskType,
                    taskScreenConfig,
                    updatedAt: new Date().toISOString(),
                };
                await taskTypeDao.setTaskType(taskType.id, updated);
                toast.success('Screen config saved');
            } catch (err) {
                log.error('Failed to save screen config', { err });
                toast.error('Failed to save screen config');
            }
        },
        [taskType],
    );

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Screen Config</CardTitle>
                        <CardDescription>
                            Configure which sections appear on the task screen and their order. When "Use Default" is
                            enabled, section-specific settings are inherited from the app-level default.
                        </CardDescription>
                        <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <span>
                                Changes made here will only appear on future tasks of this type. Existing tasks retain
                                the configuration they were created with.
                            </span>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {sections.fields.map((field, index) => (
                            <SectionRow
                                key={field.id}
                                index={index}
                                totalCount={sections.fields.length}
                                form={form}
                                defaultSections={defaultSections}
                                suppliesTypes={suppliesTypes}
                                onMoveUp={() => sections.move(index, index - 1)}
                                onMoveDown={() => sections.move(index, index + 1)}
                            />
                        ))}
                    </CardContent>
                </Card>

                <Button type="submit" disabled={form.formState.isSubmitting}>
                    {form.formState.isSubmitting ? 'Saving...' : 'Save Screen Config'}
                </Button>
            </form>
        </Form>
    );
};

type SectionRowProps = {
    index: number;
    totalCount: number;
    form: UseFormReturn<FormValues, unknown, FormValues>;
    defaultSections: TaskScreenSection[];
    suppliesTypes: EquipmentType[];
    onMoveUp: () => void;
    onMoveDown: () => void;
};

const SectionRow: FC<SectionRowProps> = ({
    index,
    totalCount,
    form,
    defaultSections,
    suppliesTypes,
    onMoveUp,
    onMoveDown,
}) => {
    const sectionType = form.watch(`sections.${index}.type`);
    const useDefault = form.watch(`sections.${index}.useDefault`);
    const defaultSection = defaultSections.find((s) => s.type === sectionType);

    return (
        <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={index === 0}
                        onClick={onMoveUp}
                    >
                        <ArrowUp className="h-3 w-3" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={index === totalCount - 1}
                        onClick={onMoveDown}
                    >
                        <ArrowDown className="h-3 w-3" />
                    </Button>
                </div>

                <span className="font-medium min-w-[100px]">{TASK_SECTION_LABELS[sectionType] ?? sectionType}</span>

                <FormField
                    control={form.control}
                    name={`sections.${index}.enabled`}
                    render={({ field }) => (
                        <FormItem className="flex items-center gap-2">
                            <FormLabel className="text-xs text-muted-foreground">Enabled</FormLabel>
                            <FormControl>
                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name={`sections.${index}.useDefault`}
                    render={({ field }) => (
                        <FormItem className="flex items-center gap-2">
                            <FormLabel className="text-xs text-muted-foreground">Use Default</FormLabel>
                            <FormControl>
                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                        </FormItem>
                    )}
                />
            </div>

            {!useDefault && (
                <SectionCustomFields
                    index={index}
                    form={form}
                    sectionType={sectionType}
                    defaultSection={defaultSection}
                    suppliesTypes={suppliesTypes}
                />
            )}
        </div>
    );
};

type SectionCustomFieldsProps = {
    index: number;
    form: UseFormReturn<FormValues, unknown, FormValues>;
    sectionType: string;
    defaultSection: TaskScreenSection | undefined;
    suppliesTypes: EquipmentType[];
};

const SectionCustomFields: FC<SectionCustomFieldsProps> = ({
    index,
    form,
    sectionType,
    defaultSection,
    suppliesTypes,
}) => {
    const slots = useFieldArray({
        control: form.control,
        name: `sections.${index}.slots`,
    });

    const suppliesConfig = useFieldArray({
        control: form.control,
        name: `sections.${index}.suppliesConfig`,
    });

    const handleRestoreDefaults = useCallback(() => {
        if (!defaultSection) return;
        form.setValue(`sections.${index}.title`, defaultSection.title ?? '');
        form.setValue(`sections.${index}.description`, defaultSection.description ?? '');
        if (defaultSection.type === 'pictures') {
            form.setValue(`sections.${index}.slots`, defaultSection.slots);
        }
        if (defaultSection.type === 'supplies') {
            form.setValue(`sections.${index}.suppliesConfig`, defaultSection.suppliesConfig);
        }
    }, [form, index, defaultSection]);

    return (
        <div className="ml-12 space-y-3 border-l-2 pl-4">
            <div className="flex justify-end">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRestoreDefaults}
                    disabled={!defaultSection}
                >
                    <RotateCcw className="h-3 w-3 mr-1" /> Restore Defaults
                </Button>
            </div>
            <FormField
                control={form.control}
                name={`sections.${index}.title`}
                render={({ field }) => (
                    <FormItem>
                        <FormLabel className="text-xs">Title</FormLabel>
                        <FormControl>
                            <Input
                                className="bg-white"
                                placeholder="Section title"
                                {...field}
                                value={field.value || ''}
                            />
                        </FormControl>
                        <FormDescription>Display title for the section.</FormDescription>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name={`sections.${index}.description`}
                render={({ field }) => (
                    <FormItem>
                        <FormLabel className="text-xs">Description</FormLabel>
                        <FormControl>
                            <Input
                                className="bg-white"
                                placeholder="Optional description..."
                                {...field}
                                value={field.value || ''}
                            />
                        </FormControl>
                        <FormDescription>Optional descriptive text shown in the section.</FormDescription>
                    </FormItem>
                )}
            />

            {sectionType === 'pictures' && (
                <div className="space-y-2">
                    <FormLabel className="text-xs">Slots</FormLabel>
                    {slots.fields.map((slotField, slotIndex) => (
                        <div key={slotField.id} className="flex items-center gap-2">
                            <FormField
                                control={form.control}
                                name={`sections.${index}.slots.${slotIndex}.category`}
                                render={({ field }) => (
                                    <FormItem className="w-28">
                                        <Select value={field.value} onValueChange={field.onChange}>
                                            <FormControl>
                                                <SelectTrigger className="w-full min-w-0 bg-white text-sm">
                                                    <SelectValue />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="before">Before</SelectItem>
                                                <SelectItem value="after">After</SelectItem>
                                                <SelectItem value="other">Other</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name={`sections.${index}.slots.${slotIndex}.title`}
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormControl>
                                            <Input
                                                className="bg-white text-sm"
                                                placeholder="Display label"
                                                {...field}
                                                value={field.value || ''}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => slots.remove(slotIndex)}
                            >
                                <CircleX className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => slots.append({ category: 'other', title: '' })}
                    >
                        <PlusCircle className="h-4 w-4 mr-1" /> Add Slot
                    </Button>
                </div>
            )}

            {sectionType === 'supplies' && (
                <div className="space-y-2">
                    <FormLabel className="text-xs">Supplies Config</FormLabel>
                    <FormDescription>
                        Configure which supply equipment types are consumed when this task is completed.
                    </FormDescription>
                    {suppliesConfig.fields.map((configField, configIndex) => (
                        <div key={configField.id} className="flex items-center gap-2">
                            <FormField
                                control={form.control}
                                name={`sections.${index}.suppliesConfig.${configIndex}.equipmentTypeId`}
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <EquipmentTypeField
                                            value={field.value ? (field.value as EquipmentTypeId) : undefined}
                                            onChange={(typeId) => field.onChange(typeId ?? '')}
                                            types={suppliesTypes}
                                            placeholder="Select supply type..."
                                        />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name={`sections.${index}.suppliesConfig.${configIndex}.quantityPerTask`}
                                render={({ field }) => (
                                    <FormItem className="w-24">
                                        <FormControl>
                                            <Input
                                                className="bg-white text-sm"
                                                type="number"
                                                min={1}
                                                placeholder="Qty"
                                                {...field}
                                                value={field.value ?? 1}
                                                onChange={(e) => field.onChange(Number(e.target.value) || 1)}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => suppliesConfig.remove(configIndex)}
                            >
                                <CircleX className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => suppliesConfig.append({ equipmentTypeId: '', quantityPerTask: 1 })}
                    >
                        <PlusCircle className="h-4 w-4 mr-1" /> Add Supply
                    </Button>
                </div>
            )}
        </div>
    );
};
